import { useState, useEffect, useCallback, useMemo } from 'react';
import { auth } from '../../../firebase/config';
import { copyToClipboard } from '../../../utils/clipboard';
import {
  fetchUserAccountWithLinkedIds,
  resolveFeedbackUserDetails,
  resolveEnvironmentLabel,
} from './feedbackAccountLoader';
import { submitFeedbackPayload } from './feedbackSubmitService';
import { logger } from '../../../services/logger';

export function useFeedbackSubmit(isOpen) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submissionResult, setSubmissionResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const [accountData, setAccountData] = useState(null);
  const [metadata, setMetadata] = useState({
    url: '',
    route: '',
    userAgent: '',
  });

  const resetFormState = useCallback(() => {
    setErrorMessage('');
    setSubmissionResult(null);
    setCopied(false);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetFormState();

      if (typeof window !== 'undefined') {
        setMetadata({
          url: window.location.href,
          route: window.location.pathname + window.location.search,
          userAgent: window.navigator.userAgent,
        });
      }

      const user = auth.currentUser;
      if (user) {
        fetchUserAccountWithLinkedIds(user).then((acc) => {
          setAccountData(acc);
        });
      }
    }
  }, [isOpen, resetFormState]);

  const user = auth.currentUser;
  const userDetails = useMemo(
    () => resolveFeedbackUserDetails(accountData, user),
    [accountData, user]
  );
  const envLabel = useMemo(() => resolveEnvironmentLabel(), []);

  const handleSubmit = useCallback(async ({ type, title, description, screenshotFile }) => {
    if (!title.trim()) {
      setErrorMessage('Please provide a brief title for your report.');
      return false;
    }

    if (!description.trim()) {
      setErrorMessage('Please provide a description.');
      return false;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const result = await submitFeedbackPayload({
        type,
        title,
        description,
        screenshotFile,
        user,
        userDetails,
        metadata,
        envLabel,
      });

      setSubmissionResult(result);
      return true;
    } catch (err) {
      logger.error('FeedbackModal: Submission error:', err);
      setErrorMessage(err.message || 'Unable to submit your report. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [user, userDetails, metadata, envLabel]);

  const handleCopyReference = useCallback((refText) => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
    copyToClipboard(refText).then((success) => {
      if (!success) {
        setCopied(false);
      }
    }).catch(() => {
      setCopied(false);
    });
  }, []);

  const referenceText = submissionResult?.issueNumber
    ? `Issue #${submissionResult.issueNumber}`
    : submissionResult?.fallbackRef || 'Submitted';

  return {
    submitting,
    errorMessage,
    setErrorMessage,
    submissionResult,
    copied,
    handleCopyReference,
    referenceText,
    userName: userDetails.userName,
    userEmail: userDetails.userEmail,
    userPhone: userDetails.userPhone,
    userRole: userDetails.userRole,
    accountId: userDetails.accountId,
    linkedAccountIds: userDetails.linkedAccountIds,
    metadata,
    envLabel,
    handleSubmit,
    resetFormState,
  };
}
